import OverlayBase from "../overlay/OverlayBase";
import {useState} from "react";
import {validateIPv4} from "../../../util/validation";
import SvgLoader from "../../../svg/SvgLoader";
import {rpc_banIp} from "../rpc";
import {useSocket} from "../SocketContext";

export default function IpBanOverlay(props: { ip?: string, hide: () => void }){

    const socket = useSocket()

    const [loading, setLoading] = useState<boolean>(false)
    const [reason, setReason] = useState<string>("")
    const [permanent, setPermanent] = useState<boolean>(true)
    const [durationValue, setDurationValue] = useState<number>(1)
    const [durationUnit, setDurationUnit] = useState<number>(86400)
    const [ip, setIp] = useState<string>(props.ip ?? "")
    const [error, setError] = useState<string>("")

    const [errors, setErrors] = useState({ reason: "", duration: "", ip: "" })

    const handleBan = async () => {
        if (loading){
            return
        }

        setError("")
        let validationErrors = { reason: "", duration: "", ip: "" };
        if (!reason || !reason.trim()) {
            validationErrors.reason = "Reason is required.";
        }
        if (!permanent && !durationValue) {
            validationErrors.duration = "Duration is required.";
        }
        if (!ip || !ip.trim()){
            validationErrors.ip = "IP address is required.";
        }
        else if (!validateIPv4(ip)){
            validationErrors.ip = "Invalid IP address. Only IPv4 addresses are supported.";
        }

        setErrors(validationErrors);
        if (validationErrors.reason || validationErrors.duration || validationErrors.ip) {
            return;
        }

        const duration = permanent ? -1 : durationUnit * durationValue;

        setLoading(true)

        const result = await rpc_banIp(socket, ip, reason, duration)

        setLoading(false)

        if (result.success){
            props.hide()
        }
        else{
            let err = `Error: ${result.message ?? "Unknown error (-1)"}`
            if (result.message === "server_not_connected"){
                err = `The game server is not connected to RustServerTool (${err})`
            }
            else if (result.message === "server_timeout"){
                err = `The IP address is already banned or the game server did not respond in time (${err})`
            }
            setError(err);
        }
    };

    return (
        <OverlayBase hide={props.hide} w="w-[28rem]">
            <h2 className="text-xl font-semibold mb-4">Ban IP</h2>
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium">IP Address</label>
                    <input
                        type="text"
                        value={ip}
                        onChange={(e) => setIp(e.target.value)}
                        placeholder={props.ip ?? "0.0.0.0"}
                        className="w-full rst-input"
                    />
                    {errors.ip && <p className="text-red-500 text-sm mt-1">{errors.ip}</p>}
                </div>
                <div>
                    <label className="block text-sm font-medium">Reason</label>
                    <input
                        type="text"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter reason for ban"
                        className="w-full rst-input"
                    />
                    {errors.reason && <p className="text-red-500 text-sm mt-1">{errors.reason}</p>}
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={permanent}
                        onChange={(e) => setPermanent(e.target.checked)}
                        className="w-4 h-4"
                    />
                    <label className="text-sm">Permanent Ban</label>
                </div>
                {!permanent && (
                    <div>
                        <label className="block text-sm font-medium">Ban Duration</label>
                        <div className="flex gap-2">
                            <input
                                type="number"
                                value={durationValue}
                                onChange={(e) => setDurationValue(parseInt(e.target.value))}
                                placeholder="Enter duration"
                                className="w-2/3 p-2 border rounded-lg text-neutral-800"
                            />
                            <select
                                value={durationUnit}
                                onChange={(e) => setDurationUnit(parseInt(e.target.value))}
                                className="w-1/3 p-2 border rounded-lg text-neutral-800"
                            >
                                <option value="60">Minutes</option>
                                <option value="3600">Hours</option>
                                <option value="86400">Days</option>
                            </select>
                        </div>
                        {errors.duration && <p className="text-red-500 text-sm mt-1">{errors.duration}</p>}
                    </div>
                )}
            </div>
            <div className="mt-6 flex flex-col items-end">
                {error && <p className="text-red-500 text-sm mb-2 text-right">{error}</p>}
                <div className="flex justify-end gap-3">
                    <button onClick={props.hide}
                            className="px-4 py-2 hover:bg-neutral-700 border border-neutral-500 rounded-lg">Cancel
                    </button>
                    <button onClick={handleBan} disabled={loading}
                            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg disabled:bg-neutral-600 flex items-center">
                        {loading ? <SvgLoader className="size-5 text-transparent fill-white text-neutral-700 animate-spin inline" /> : "Ban"}
                    </button>
                </div>
            </div>
        </OverlayBase>
    )
}